import React, { useState } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import {
  Typography,
  Stack,
  IconButton,
  TextField,
  Box,
  ToggleButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  ListItemText,
  List,
  ListItem,
  Divider,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import "Components/Styles/carousel.css";
import useMediaQuery from "@mui/material/useMediaQuery";

//icons
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Flavor, PresaleItem } from "Model/PresaleItem";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { updateItemByCount } from "Redux/actionCreators";
import { selectCartItems } from "Redux/selectors";
import _ from "lodash";
import { InfoBox } from "Components/AllComponents";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  PresaleOrderItemCustomization,
  defaultPresaleOrderItemCustomization,
} from "Model/PresaleOrder";
import {
  PERSONALIZATION_TYPE_BOTH,
  PERSONALIZATION_TYPE_CUSTOM_ONLY,
  PERSONALIZATION_TYPE_PRESET_ONLY,
} from "Constants";

export const PresaleItems = ({
  presaleItems,
}: {
  presaleItems: PresaleItem[];
}) => {
  const dispatch = useAppDispatch();
  const cartItems = useAppSelector(selectCartItems);
  const [openAddName, setAddName] = useState(false);
  const [currentItemId, setCurrentItemId] = useState("");
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [currentPersonalization, setCurrentPersonalization] = useState("");

  const [currentFlavor, setCurrentFlavor] = useState<Flavor>({
    flavorPrice: 0,
    flavorName: "",
  }); // Use the Flavor type for currentFlavor
  const [currentColor, setCurrentColor] = useState("");

  // dont touch it, gpt generated
  const generateLabel = (
    p: PresaleOrderItemCustomization,
    item: PresaleItem
  ) => {
    // Find the flavor object to get the price
    const flavorObj = item.flavors.find((f) => f.flavorName === p.flavor);
    const flavorDisplay =
      p.flavor && flavorObj
        ? `${flavorObj.flavorName}${flavorObj.flavorPrice !== 0 ? ` (+$${flavorObj.flavorPrice})` : ""}`
        : p.flavor;

    const result = `${p.personalization}${
      flavorDisplay ? `${p.personalization ? ", " : ""}${flavorDisplay}` : ""
    }${
      p.color && !p.flavor ? `${p.personalization ? ", " : ""}${p.color}` : ""
    }${p.flavor && p.color ? `, ${p.color}` : ""}`;

    return result;
  };

  const flavorToDisplay = (item: PresaleItem) => {
    if (
      currentItemId === item.id &&
      currentFlavor &&
      currentFlavor.flavorName !== ""
    ) {
      return `$${item.price + currentFlavor?.flavorPrice}`;
    }
    if (item.flavors.length > 0) {
      const flavorPrices = item.flavors.map((f) => f.flavorPrice);
      const minPrice = item.price + Math.min(...flavorPrices);
      const maxPrice = item.price + Math.max(...flavorPrices);
      const priceRange =
        minPrice !== maxPrice ? `$${minPrice} - $${maxPrice}` : `$${minPrice}`;
      return priceRange;
    } else {
      return `$${item.price}`;
    }
  };

  const theme = useTheme();
  const colors = {
    frontColor: theme.palette.primary.main,
    backColor: theme.palette.background.default,
  };

  const customizable = (item: PresaleItem) =>
    (item.personalizable &&
      !(
        item.personalizationType === PERSONALIZATION_TYPE_PRESET_ONLY &&
        item.personalizationOptions.length === 0
      )) ||
    item.flavors.length > 0 ||
    item.colors.length > 0;

  const backgroundPattern = `
    repeating-linear-gradient(
      -45deg,
      ${colors.frontColor},
      ${colors.frontColor} 8px,
      ${colors.backColor} 8px,
      ${colors.backColor} 64px
    )
  `;

  // item images
  const Carousel = require("react-responsive-carousel").Carousel;

  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const isDrawerVisible = useMediaQuery("(min-width: 900px)");

  const [revealedItems, setRevealedItems] = useState<Record<string, boolean>>(
    {}
  );
  const handleRevealItem = (itemId: string) => {
    setRevealedItems((prev) => ({
      ...prev,
      [itemId]: true,
    }));
  };

  return (
    //actual content width: <640, 640-767, 768-991, 992-1199, 1200-1399, >=1400
    <ResponsiveMasonry
      columnsCountBreakPoints={
        isDrawerVisible
          ? {
              // With drawer: add drawer width to content breakpoints
              1029: 1, // 639 + 390 (for 640px content)
              1157: 2, // 767 + 390 (for 768px content)
              1381: 3, // 991 + 390 (for 992px content)
              1589: 4, // 1199 + 390 (for 1200px content)
              1789: 5, // 1399 + 390 (for 1400px content)
            }
          : {
              // No drawer: use content width directly
              639: 1, // up to 640px
              767: 2, // 640-768px
              991: 3, // 768-992px
              1199: 4, // 992-1200px
              1399: 5, // 1200-1400px
            }
      }
    >
      <Masonry gutter="24px">
        {presaleItems.map((item, index) => (
          <Stack
            key={item.id}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              boxShadow:
                cartItems[item.id]?.count > 0
                  ? "rgba(0, 0, 0, 0.2) 0px 4px 12px 0px"
                  : "none",
              outline: cartItems[item.id]?.count > 0 ? "2px solid" : "none",
              outlineColor:
                cartItems[item.id]?.count > 0 ? "primary.main" : "transparent",
            }}
          >
            {/* image with sensitive content blur */}
            {item.img ? (
              <Box sx={{ position: "relative", overflow: "hidden" }}>
                <img
                  style={{
                    width: "100%",
                    display: "block",
                    filter:
                      item.sensitive && !revealedItems[item.id]
                        ? "blur(50px)"
                        : "none",
                  }}
                  src={item.img}
                />
                {item.sensitive && !revealedItems[item.id] && (
                  <Box
                    onClick={() => handleRevealItem(item.id)}
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <VisibilityOffOutlinedIcon
                      fontSize="large"
                      sx={{ mb: 1, color: "white" }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        mb: 2,
                        textAlign: "center",
                      }}
                    >
                      Sensitive Content
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "white",
                        color: "black",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                        },
                      }}
                    >
                      Show Image
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  aspectRatio: "1/1",
                  backgroundColor: "background.default",
                  opacity: 0.04,
                  background: backgroundPattern,
                }}
              />
            )}

            <Stack
              sx={{
                p: 2,
                backgroundColor: "white",
              }}
            >
              {/* title & description */}
              <Stack>
                {item.title && (
                  <Typography variant="subtitle2">{item.title}</Typography>
                )}
                {item.description && (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ whiteSpace: "pre-line" }}
                  >
                    {item.description}
                  </Typography>
                )}
              </Stack>

              {/* price & quantity */}
              <Stack
                direction="row"
                alignItems="center"
                flex={1}
                sx={{
                  height: "40px",
                }}
              >
                <Stack flex={1}>
                  <Typography variant="subtitle2">${item.price}</Typography>
                </Stack>
                {item.soldOut ? (
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    SOLD OUT
                  </Typography>
                ) : (
                  <Stack direction="row" alignItems="center">
                    {cartItems[item.id]?.count > 1 &&
                    !customizable(item) &&
                    editingItemId !== item.id ? (
                      <Box
                        sx={{
                          backgroundColor: "rgba(0,0,0,0.04)",
                          borderRadius: 100,
                        }}
                      >
                        <IconButton
                          size="medium"
                          disabled={openAddName}
                          onClick={() => {
                            setCurrentItemId(item.id);
                            setCurrentItemIndex(index);
                            dispatch(
                              updateItemByCount({
                                item: cartItems[item.id],
                                deltaCount: -1,
                                customizable: false,
                                customization:
                                  defaultPresaleOrderItemCustomization,
                                customizationToDeleteIndex:
                                  cartItems[item.id].count - 1,
                              })
                            );
                          }}
                        >
                          <RemoveRoundedIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    ) : cartItems[item.id]?.count === 1 &&
                      !customizable(item) &&
                      editingItemId !== item.id ? (
                      <Box
                        sx={{
                          backgroundColor: "rgba(0,0,0,0.04)",
                          borderRadius: 100,
                        }}
                      >
                        <IconButton
                          size="medium"
                          disabled={openAddName}
                          onClick={() => {
                            setCurrentItemId(item.id);
                            setCurrentItemIndex(index);
                            dispatch(
                              updateItemByCount({
                                item: cartItems[item.id],
                                deltaCount: -1,
                                customizable: false,
                                customization:
                                  defaultPresaleOrderItemCustomization,
                                customizationToDeleteIndex:
                                  cartItems[item.id].count - 1,
                              })
                            );
                          }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    ) : null}

                    {cartItems[item.id]?.count > 0 ? (
                      editingItemId === item.id ? (
                        <TextField
                          type="number"
                          size="small"
                          autoFocus
                          value={editingValue}
                          disabled={openAddName || customizable(item)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEditingValue(value);
                            const newCount = parseInt(value);
                            if (!isNaN(newCount) && newCount > 0) {
                              const delta = newCount - cartItems[item.id].count;
                              setCurrentItemId(item.id);
                              setCurrentItemIndex(index);
                              dispatch(
                                updateItemByCount({
                                  item: cartItems[item.id],
                                  deltaCount: delta,
                                  customizable: false,
                                  customization: {
                                    price: item.price,
                                    flavor: "",
                                    color: "",
                                    personalization: "",
                                    count: 1,
                                  },
                                  customizationToDeleteIndex:
                                    delta < 0
                                      ? cartItems[item.id].count - 1
                                      : -1,
                                })
                              );
                            }
                          }}
                          onWheel={(e: any) => e.target.blur()}
                          onBlur={() => {
                            setEditingItemId(null);
                            setEditingValue("");
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === "Escape") {
                              setEditingItemId(null);
                              setEditingValue("");
                            }
                          }}
                          onFocus={(e) => e.target.select()}
                          inputProps={{
                            min: 1,
                            style: { textAlign: "center" },
                          }}
                          sx={{
                            width: "72px",
                            mr: 2,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "rgba(0,0,0,0.12)",
                              },
                            },
                          }}
                        />
                      ) : (
                        <Typography
                          variant="subtitle2"
                          textAlign="center"
                          onClick={() => {
                            if (!openAddName && !customizable(item)) {
                              setEditingItemId(item.id);
                              setEditingValue(
                                cartItems[item.id]?.count.toString()
                              );
                            }
                          }}
                          sx={{
                            maxWidth: "20px",
                            ml: 2,
                            mr: 2,
                            lineHeight: 1,
                            cursor:
                              openAddName || customizable(item)
                                ? "default"
                                : "pointer",
                            "&:hover": {
                              textDecoration:
                                openAddName || customizable(item)
                                  ? "none"
                                  : "underline",
                            },
                          }}
                        >
                          {cartItems[item.id]?.count}
                        </Typography>
                      )
                    ) : null}

                    {editingItemId !== item.id && (
                      <Box
                        sx={{
                          backgroundColor: "rgba(0,0,0,0.04)",
                          borderRadius: 100,
                        }}
                      >
                        <IconButton
                          size="medium"
                          color="secondary"
                          disabled={openAddName}
                          onClick={() => {
                            setCurrentPersonalization(
                              item.personalizable &&
                                item.personalizationType ===
                                  PERSONALIZATION_TYPE_PRESET_ONLY &&
                                item.personalizationOptions.length > 0
                                ? item.personalizationOptions[0]
                                : ""
                            );
                            setCurrentItemId(item.id);
                            setCurrentItemIndex(index);
                            if (customizable(item)) {
                              setAddName(true);
                              setCurrentFlavor(item.flavors[0]);
                              setCurrentColor(item.colors[0]);
                            } else {
                              dispatch(
                                updateItemByCount({
                                  item: cartItems[item.id],
                                  deltaCount: 1,
                                  customizable: false,
                                  customization: {
                                    price: item.price,
                                    flavor: "",
                                    color: "",
                                    personalization: "",
                                    count: 1,
                                  },
                                  customizationToDeleteIndex: -1,
                                })
                              );
                            }
                          }}
                        >
                          <AddRoundedIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    )}
                  </Stack>
                )}
              </Stack>

              {/* personalization, options, and added customizations - combined */}
              {customizable(item) &&
              ((openAddName && index === currentItemIndex) ||
                cartItems[item.id]?.customization.length > 0) ? (
                <InfoBox
                  sx={{
                    mt: 1,
                    py: openAddName && index === currentItemIndex ? 1 : 0,
                  }}
                >
                  <Stack>
                    {/* personalization form */}
                    {openAddName && index === currentItemIndex ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          dispatch(
                            updateItemByCount({
                              item: cartItems[currentItemId],
                              deltaCount: 1,
                              customizable: customizable(
                                presaleItems[currentItemIndex]
                              ),
                              customization: {
                                price:
                                  item.flavors.length > 0
                                    ? item.price + currentFlavor.flavorPrice
                                    : item.price,
                                flavor:
                                  item.flavors.length > 0
                                    ? currentFlavor.flavorName
                                    : "",
                                color:
                                  item.colors.length > 0 ? currentColor : "",
                                personalization: item.personalizable
                                  ? currentPersonalization
                                  : "",
                                count: 1,
                              },
                              customizationToDeleteIndex: -1,
                            })
                          );
                          setAddName(false);
                          setCurrentPersonalization("");
                          setCurrentFlavor({
                            flavorPrice: 0,
                            flavorName: "",
                          });
                        }}
                      >
                        <Stack>
                          {item.personalizable &&
                          item.personalizationType ===
                            PERSONALIZATION_TYPE_CUSTOM_ONLY ? (
                            <TextField
                              autoFocus
                              fullWidth
                              required
                              size="small"
                              margin="dense"
                              placeholder="Free text"
                              value={currentPersonalization}
                              onChange={(e) => {
                                setCurrentPersonalization(e.target.value);
                              }}
                            />
                          ) : null}
                          {item.personalizable &&
                          item.personalizationType ===
                            PERSONALIZATION_TYPE_BOTH ? (
                            <Autocomplete
                              freeSolo
                              size="small"
                              options={item.personalizationOptions}
                              onChange={(
                                event: any,
                                newValue: string | null
                              ) => {
                                if (newValue) {
                                  setCurrentPersonalization(newValue);
                                }
                              }}
                              inputValue={currentPersonalization}
                              onInputChange={(event, newInputValue) => {
                                setCurrentPersonalization(newInputValue);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  required
                                  label="Text"
                                  margin="dense"
                                  placeholder="Choose or enter free text"
                                  {...params}
                                />
                              )}
                            />
                          ) : null}
                          {item.personalizable &&
                          item.personalizationType ===
                            PERSONALIZATION_TYPE_PRESET_ONLY &&
                          item.personalizationOptions.length > 0 ? (
                            <FormControl fullWidth size="small" margin="dense">
                              <InputLabel>Text</InputLabel>
                              <Select
                                value={currentPersonalization}
                                defaultValue={item.personalizationOptions[0]}
                                label="Text"
                                onChange={(e) => {
                                  setCurrentPersonalization(e.target.value);
                                }}
                              >
                                {item.personalizationOptions.map(
                                  (option, index) => (
                                    <MenuItem
                                      key={option + index}
                                      value={option}
                                    >
                                      {option}
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </FormControl>
                          ) : null}
                          {item.flavors.length > 0 ? (
                            <FormControl fullWidth size="small" margin="dense">
                              <InputLabel>Flavor</InputLabel>
                              <Select
                                value={currentFlavor.flavorName}
                                label="Flavor"
                                onChange={(e) => {
                                  const selectedFlavor = item.flavors.find(
                                    (flavor) =>
                                      flavor.flavorName === e.target.value
                                  );
                                  if (selectedFlavor) {
                                    setCurrentFlavor(selectedFlavor);
                                  }
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-input.MuiSelect-select":
                                    {
                                      whiteSpace: "normal",
                                    },
                                }}
                              >
                                {item.flavors.map((f) => (
                                  <MenuItem
                                    key={f.flavorName}
                                    value={f.flavorName}
                                    sx={{ whiteSpace: "normal" }}
                                  >
                                    {f.flavorName}{" "}
                                    {f.flavorPrice !== 0 &&
                                      `(+$${f.flavorPrice})`}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : null}
                          {item.colors.length > 0 ? (
                            <FormControl fullWidth size="small" margin="dense">
                              <InputLabel>Design</InputLabel>
                              <Select
                                value={currentColor}
                                label="Design"
                                onChange={(e) => {
                                  setCurrentColor(e.target.value);
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-input.MuiSelect-select":
                                    {
                                      whiteSpace: "normal",
                                    },
                                }}
                              >
                                {item.colors.map((color) => (
                                  <MenuItem
                                    key={color}
                                    value={color}
                                    sx={{ whiteSpace: "normal" }}
                                  >
                                    {color}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : null}
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-end"
                            spacing={1}
                            mt={1}
                          >
                            <Button
                              onClick={() => {
                                setAddName(false);
                                setCurrentPersonalization("");
                                setCurrentFlavor({
                                  flavorPrice: 0,
                                  flavorName: "",
                                });
                              }}
                            >
                              Cancel
                            </Button>
                            <Button type="submit">Add</Button>
                          </Stack>
                        </Stack>
                        {cartItems[item.id]?.customization.length > 0 && (
                          <Divider sx={{ mt: 1 }} />
                        )}
                      </form>
                    ) : null}

                    {/* added customizations list */}
                    {cartItems[item.id]?.customization.length > 0 ? (
                      <List
                        disablePadding
                        sx={{
                          width: "100%",
                          mt: openAddName && index === currentItemIndex ? 1 : 0,
                        }}
                      >
                        {cartItems[item.id]?.customization.map((p, index) => (
                          <ListItem
                            divider={
                              index !==
                              cartItems[item.id]?.customization.length - 1
                            }
                            key={index}
                            disableGutters
                            secondaryAction={
                              <IconButton
                                size="small"
                                aria-label="delete"
                                disabled={openAddName}
                                onClick={() => {
                                  dispatch(
                                    updateItemByCount({
                                      item: cartItems[item.id],
                                      deltaCount: -1,
                                      customizable: customizable(item),
                                      customization:
                                        cartItems[item.id]?.customization[
                                          index
                                        ],
                                      customizationToDeleteIndex: index,
                                    })
                                  );
                                }}
                              >
                                <DeleteOutlineRoundedIcon />
                              </IconButton>
                            }
                          >
                            <ListItemText primary={generateLabel(p, item)} />
                          </ListItem>
                        ))}
                      </List>
                    ) : null}
                  </Stack>
                </InfoBox>
              ) : null}

              {/* Item total - shows for all items in cart */}
              {cartItems[item.id]?.count > 0 && (
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Total: ${cartItems[item.id]?.itemTotalPrice}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        ))}
      </Masonry>
    </ResponsiveMasonry>
  );
};
